import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { QrAssembler, QR_FRAME_PREFIX, QR_FRAME_PREFIX_V2 } from "@psplit/ledger-core";
import { startScanner, type ScannerHandle } from "../lib/qrcam.js";
import { errorMessage } from "../lib/errors.js";
import { Modal } from "./Modal.js";

// Shows one QR frame, or cycles through frames for multi-part payloads.
export function QrShow({ frames, caption }: { frames: string[]; caption?: string }) {
  const [index, setIndex] = useState(0);
  const [dataUrl, setDataUrl] = useState("");

  useEffect(() => {
    if (frames.length <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % frames.length), 450);
    return () => clearInterval(timer);
  }, [frames.length]);

  useEffect(() => {
    let cancelled = false;
    const frame = frames[index] ?? frames[0] ?? "";
    if (frame === "") return;
    void QRCode.toDataURL(frame, { errorCorrectionLevel: "L", margin: 1, scale: 6 }).then((url) => {
      if (!cancelled) setDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [frames, index]);

  return (
    <div className="qrshow">
      {dataUrl !== "" && <img src={dataUrl} alt="QR code" />}
      {frames.length > 1 && (
        <div className="qrmeta">
          frame {index + 1} of {frames.length} - keep the camera on it, frames repeat
        </div>
      )}
      {caption !== undefined && <div className="qrmeta">{caption}</div>}
    </div>
  );
}

// Scans QR frames until a full payload assembles, then reports it once.
export function QrScan({
  onPayload,
  onClose,
}: {
  onPayload: (payload: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [progress, setProgress] = useState("point the camera at the QR");
  const [error, setError] = useState("");

  // Callers pass inline arrows; keeping the callback in a ref lets the scanner
  // effect run once per mount. With onPayload in the deps, every setProgress
  // re-render would tear down the camera and RESET multi-frame assembly.
  const onPayloadRef = useRef(onPayload);
  useEffect(() => {
    onPayloadRef.current = onPayload;
  });

  useEffect(() => {
    const assembler = new QrAssembler();
    let handle: ScannerHandle | null = null;
    let finished = false;
    const video = videoRef.current;
    if (!video) return;

    void startScanner(video, (text) => {
      if (finished) return;
      const status = assembler.add(text);
      if (!status.ok) {
        // Raw single-frame payloads (invite URLs) are not PS frames - pass through.
        const isFrame =
          text.startsWith(`${QR_FRAME_PREFIX}|`) || text.startsWith(`${QR_FRAME_PREFIX_V2}|`);
        if (text.includes("ps=1&") || !isFrame) {
          finished = true;
          handle?.stop();
          onPayloadRef.current(text);
          return;
        }
        setError(status.error ?? "unreadable frame");
        return;
      }
      setProgress(`frames: ${status.have}/${status.total}`);
      if (status.done) {
        finished = true;
        handle?.stop();
        try {
          onPayloadRef.current(assembler.payload());
        } catch (e) {
          setError(errorMessage(e));
        }
      }
    })
      .then((h) => {
        handle = h;
        if (finished) h.stop();
      })
      .catch((e: unknown) => {
        setError(errorMessage(e));
      });

    return () => {
      finished = true;
      handle?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per mount; callback lives in a ref
  }, []);

  return (
    <Modal onClose={onClose}>
      <video ref={videoRef} className="qrvideo" muted playsInline />
      <div className="qrmeta">{progress}</div>
      {error !== "" && <div className="error">{error}</div>}
      <button className="btn" onClick={onClose}>
        Close
      </button>
    </Modal>
  );
}
